import type { ComponentProps, JSX } from 'react'

import { useSplitEnabled } from '@buffer-mono/features'
import { Button, Notice } from '@buffer-mono/popcorn'

import { useHasOrganizationAction } from '~publish/contexts/AccountContext'
import { getChannelNameForDisplay } from '~publish/helpers/getChannelNameForDisplay'
import { useRefreshChannelConnection } from '~publish/hooks/useRefreshChannelConnection'

import type { ChannelMask } from '../hooks/useChannelsForSettings'
import { getChannelRefreshNotices } from '../utils/getChannelRefreshNotices'
import type { ChannelRefreshNoticeType } from '../utils/getChannelRefreshNotices'

type NoticeVariant = ComponentProps<typeof Notice>['variant']

const noticeContent: Record<
  ChannelRefreshNoticeType,
  { variant: NoticeVariant; heading: string; text: string }
> = {
  xMigration: {
    variant: 'error',
    heading: 'Action required for X (Twitter)',
    text: 'Refresh your X (Twitter) connection to continue publishing and accessing analytics.',
  },
  linkedinUpgrade: {
    variant: 'warning',
    heading: 'Important LinkedIn update',
    text: 'Our LinkedIn integration has enhanced for better performance. Refresh your connection today to keep your posts going out smoothly.',
  },
  pinterestUpgrade: {
    variant: 'warning',
    heading: 'Important Pinterest update',
    text: 'Our Pinterest integration has enhanced for better performance. Refresh your connection today to keep your posts going out smoothly.',
  },
  tiktokUpgrade: {
    variant: 'warning',
    heading: 'Important TikTok update',
    text: 'Our TikTok integration has enhanced for better performance. Refresh your connection today to keep your posts going out smoothly.',
  },
  igLoginRefresh: {
    variant: 'info',
    heading: 'More metrics for your Instagram posts are here!',
    text: 'Refresh your connection, then visit the Sent tab or your past posts to see how they performed.',
  },
}

const channelListForDisplay = (channels: ChannelMask[]): string =>
  channels.map((channel) => getChannelNameForDisplay(channel)).join(', ')

export const ChannelRefreshNotices = ({
  channels,
}: {
  channels: ChannelMask[]
}): JSX.Element | null => {
  const { isEnabled: isIgLoginRefreshEnabled } = useSplitEnabled(
    'ig-login-refresh-banner',
  )
  const canManageChannels = useHasOrganizationAction('manageChannels')
  const refreshChannelConnection = useRefreshChannelConnection()

  const notices = getChannelRefreshNotices(channels, {
    isIgLoginRefreshEnabled,
  })

  if (notices.length === 0) {
    return null
  }

  return (
    <>
      {notices.map(({ type, channels: affectedChannels }) => {
        const { variant, heading, text } = noticeContent[type]
        const showRefreshAction =
          canManageChannels && affectedChannels.length === 1

        return (
          <Notice key={type} variant={variant}>
            <Notice.Heading>{heading}</Notice.Heading>
            <Notice.Text>
              {text} Affected: {channelListForDisplay(affectedChannels)}.
              {!showRefreshAction &&
                canManageChannels &&
                ' Use the Refresh button next to each affected channel below.'}
            </Notice.Text>
            {showRefreshAction && (
              <Notice.Actions>
                <Button
                  variant={variant === 'error' ? 'critical' : 'secondary'}
                  onClick={(): void => {
                    refreshChannelConnection({
                      channel: affectedChannels[0],
                      cta: `publish-settingsChannels-${type}Notice-refreshConnection-1`,
                    })
                  }}
                >
                  Refresh Connection
                </Button>
              </Notice.Actions>
            )}
          </Notice>
        )
      })}
    </>
  )
}
